<template>
  <div data-testid="planned-allocation-chart">
    <Grid :columns="[2, 1]" gutter="m">
      <section>
        <HeadingDisplay size="extra-small" :head="headingData.head" title="">
          <template #title-component>
            <Money>{{ headingData.value }}</Money>
          </template>
        </HeadingDisplay>
        <BarChart
          v-if="series"
          data-testid="bar-chart"
          use-allocation-options
          height="314"
          :series="series"
          :labels="[]"
        />
      </section>
      <AllocationChartLegend :data="legendData || []" />
    </Grid>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { HeadingDisplay, Grid } from '@warrenbrasil/nebraska-web';
import { AllocationChartLegend } from '@/modules/wealth/components/AllocationChartLegend';
import { BarChart } from '@/modules/common/components/charts/v2/BarChart';
import { Money } from '@/modules/common/components/Money';

import { IPlannedChart } from '@/modules/wealth/services/portfolio-allocation';

interface Props {
  plannedChart: IPlannedChart;
}

const { plannedChart } = defineProps<Props>();

const headingData = ref({
  head: 'Patrimônio da carteira',
  value: plannedChart.totalBalance.formatted
});

const legendData = computed(() => {
  return plannedChart.planned.map((item, index) => {
    return {
      color: item.color,
      leftTitle: item.type,
      leftSubtitle: `${plannedChart.applied[index].percent.formatted} (A)`,
      rightTitle: '',
      rightSubtitle: `${item.percent.formatted} ${item.sufix}`
    };
  });
});

const series = computed(() => {
  return [
    {
      type: 'column',
      name: 'Atual',
      data: plannedChart.applied.map(item => {
        return {
          y: item.percent.value,
          custom: {
            head: `${item.type} atual`,
            value: item.percent.formatted
          }
        };
      }),
      colors: plannedChart.applied.map(item => item.color),
      ...serieConfig('A'),
      point: {
        events: {
          mouseOver: function () {
            headingData.value = this.custom;
          },
          mouseOut: function () {
            headingData.value = {
              head: 'Patrimônio da carteira',
              value: plannedChart.totalBalance.formatted
            };
          }
        }
      }
    },
    {
      type: 'column',
      name: 'Planejada',
      data: plannedChart.planned.map(item => {
        return {
          y: item.percent.value,
          custom: {
            head: `${item.type} planejada`,
            value: item.percent.formatted
          }
        };
      }),
      colors: plannedChart.planned.map(item => item.color),
      ...serieConfig('P'),
      point: {
        events: {
          mouseOver: function () {
            headingData.value = this.custom;
          },
          mouseOut: function () {
            headingData.value = {
              head: 'Patrimônio da carteira',
              value: plannedChart.totalBalance.formatted
            };
          }
        }
      }
    }
  ];
});

function serieConfig(format: string) {
  return {
    colorByPoint: true,
    pointPadding: 0.03,
    groupPadding: 0.05,
    maxPointWidth: 125,
    borderRadius: 5,
    inactiveOtherPoints: true,
    cursor: 'pointer',
    dataLabels: {
      enabled: true,
      format,
      style: {
        color: NebraskaColors.elementPrimary,
        fontSize: '13px',
        fontFamily: 'var(--font-text)'
      }
    }
  };
}
</script>

<style lang="less" scoped>
:deep(.heading-display__title) {
  color: @element-secondary !important;
}

:deep(.highcharts-series-0 .highcharts-point) {
  opacity: 0.5;

  &.highcharts-point-hover {
    opacity: 1;
  }

  &.highcharts-point-inactive {
    opacity: 0.2;
  }
}

:deep(.highcharts-grid-line) {
  display: none;
}
</style>
